import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconLink } from '@tabler/icons-react';
import { MaskedCurrency, maskValue } from './MaskedValue';
import { resolveAmountColor } from './AmountDisplay';
import { useMasking } from './MaskingProvider.jsx';
import { Segmented } from './ds';
import { historyRange, monthLabel, netTrend, shortMonthLabel } from '../utils/costOfLiving';
import { baselineNetCents, everythingCents, outliersCents } from '../utils/dashboardN';
import { formatCurrency } from '../utils/decimal';
import { interpolate } from '../utils/entryType';
import FigureNote from './dashboard/FigureNote';

// DESIGN_SYSTEM.md 8.17: one baseline, placed where the window's range puts it.
const CHART_HEIGHT = 160;
// Clear space kept above the tallest bar and below the deepest one.
const PAD = 2;
const BAR_RADIUS = 4;
// Bars sit this far inside their slot on each side.
const BAR_INSET = 3;
// Non-anchor months recede so the previous, finished month leads.
const DIM_OPACITY = 0.55;
// The current month so far is fainter still, so it never passes for a finished
// month (#1982).
const CURRENT_OPACITY = 0.25;
// One caption line; the caption row keeps room for four in either OT toggle
// state (#2026), so neither hovering nor the toggle ever moves the chart.
const CAPTION_LINE = 18;
// The ledger line OT chip's size (DESIGN_SYSTEM.md 8.15), reused by the toggle.
const CHIP_WIDTH = 32;
const CHIP_HEIGHT = 20;

/**
 * A month's net one-time money, signed like every bucket sum: one-time out
 * (negative) plus one-time in, so a refund of a one-time purchase offsets it
 * (#1975). Negative is net one-time spend.
 */
export const oneTimeNetCents = (m) => (m.oneTimeOutCents || 0) + (m.oneTimeInCents || 0);

/**
 * What a month's bar draws (#1978): Net, or with the OT toggle on Net + OT,
 * signed, so net one-time income raises it and net one-time spend lowers it.
 */
export const barCents = (m, overlay) => (m.netCents || 0) + (overlay ? oneTimeNetCents(m) : 0);

/**
 * The greatest and the least of what `valueOf` draws over the months with
 * lines, passed through zero.
 */
const scaleOf = (months, valueOf) => {
  let top = 0;
  let bottom = 0;
  months.forEach((m) => {
    if ((m.lineCount || 0) === 0) return;
    const cents = valueOf(m);
    top = Math.max(top, cents);
    bottom = Math.min(bottom, cents);
  });
  return { top, bottom };
};

/**
 * The chart's range (#2008): the greatest and the least of what the bars draw
 * over the months with lines, passed through zero. It follows the OT toggle:
 * off, Net alone sets it, so one large one-time entry cannot flatten the Net
 * bars; on, Net + OT does and the sums get the full height.
 * @param {Array<{netCents: number, lineCount: number}>} months
 * @param {boolean} overlay  the OT toggle's state
 * @returns {{top: number, bottom: number}} cents, top >= 0 >= bottom
 */
export const chartScale = (months, overlay) => scaleOf(months, (m) => barCents(m, overlay));

/**
 * True when the caption over slot `index` of `count` is right-justified: the
 * right half of the window, the current month included (#1978).
 */
export const alignsRight = (index, count) => index >= Math.floor(count / 2);

/**
 * The chart's gridlines (#2206): about five steps across what the bars draw,
 * on round numbers. The step is the range (top less bottom, zero included)
 * over five, rounded up to 1, 2, 2.5, 5 or 10 times a power of ten; a line
 * sits at every multiple of the step inside the range. Zero has none: the
 * baseline marks it.
 * @param {number} top     cents, >= 0
 * @param {number} bottom  cents, <= 0
 * @returns {number[]} cents: the positives rising, then the negatives falling
 */
export const gridSteps = (top, bottom) => {
  const range = top - bottom;
  if (range <= 0) return [];
  const raw = range / 5;
  const power = 10 ** Math.floor(Math.log10(raw));
  const q = raw / power;
  const step = (q <= 1 ? 1 : q <= 2 ? 2 : q <= 2.5 ? 2.5 : q <= 5 ? 5 : 10) * power;
  const lines = [];
  for (let i = 1; i * step <= top; i += 1) lines.push(i * step);
  for (let i = 1; -i * step >= bottom; i += 1) lines.push(-i * step);
  return lines;
};

/**
 * A gridline's label (#2206): "$500", "$1.5k", "($2k)", negatives in
 * accounting parens like every amount.
 */
const gridLabel = (cents) => {
  const dollars = Math.abs(cents) / 100;
  const text = dollars >= 1000
    ? `$${(dollars / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`
    : `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return cents < 0 ? `(${text})` : text;
};

/**
 * The last 24 months and the current one so far (#1942, #1982, #1978, epic
 * #1936; DESIGN_SYSTEM.md 8.17): one signed bar per month, Excess above the
 * baseline in the positive color, Overspent below in the negative one. The
 * baseline sits where the window's greatest and least drawn values put it, so
 * the chart uses its full height in either toggle state (#2008). The anchor
 * month (the previous, finished one) is
 * solid, older months dimmed, and the current month so far fainter still at
 * the right end. A month with no lines keeps its slot with no bar, so the axis
 * never shifts as history arrives. Dashed gridlines (#2206) mark round
 * amounts on the same scale, each labeled at the left edge; the labels hide
 * while masking is on.
 *
 * The caption names the pinned month (clicked or tapped; another click on it
 * unpins), else the hovered one, else the anchor, in two lines: the month and
 * Net, values colored by sign. With the OT toggle on, OT and Net + OT follow
 * as two more lines (#2005). The caption sits at the top of a row that keeps
 * room for all four in either state (#2026). Over the left half it starts at
 * the bar's left side; over the right half it ends at the bar's right side.
 * "as table" swaps in the same values for screen readers, with the window's
 * dates as its title and One-time and Net + OT always shown (#2001).
 *
 * A thin dim trend line (#1982) is the least-squares fit over the finished
 * months with lines, of whatever the bars draw; the table view states the Net
 * fit as "Trend: up $X a month".
 *
 * The OT toggle (#1978) swaps every bar to Net + OT, and the scale follows it
 * (#2008), so bars and baseline move together when it flips. Its state lives
 * in the Cost of living card, so the card's ledger flips with the chart
 * (#2025).
 *
 * Dashboard_N mode (#2139, epic #2138), on when `onSeriesChange` is passed:
 * a heading ("Net by month", a subtitle naming the series) with a Baseline /
 * Raw control in place of the OT toggle. Baseline draws Baseline net in the
 * positive and negative colors; Raw draws Net with transfers and outliers
 * counted in orange. The caption names both for the shown month on either
 * tab; the table lists Month, Baseline, Transfers, Outliers and Raw; the
 * trend follows the active tab. The "Link to Month view" chip, off by
 * default, couples the chart to the Month view card: on, `linkedYearMonth`
 * is the lit bar and picking a bar calls `onPickMonth` instead of pinning;
 * clicking the lit bar does not unpin it. The classic Dashboard passes none
 * of these and is unchanged.
 *
 * @param {object} props
 * @param {Array<{yearMonth: string, netCents: number, lineCount: number,
 *   oneTimeOutCents?: number, oneTimeInCents?: number}>} props.months  oldest first
 * @param {string} props.anchorYearMonth  the month drawn solid (the previous one)
 * @param {string} props.currentYearMonth the current month so far, last in `months`
 * @param {Object} props.strings          engine strings
 * @param {string} props.locale
 * @param {boolean} props.overlay         the OT toggle's state
 * @param {(on: boolean) => void} props.onOverlayChange  called when the toggle flips
 * @param {('baseline'|'raw')} [props.series]            Dashboard_N: the active tab
 * @param {(series: string) => void} [props.onSeriesChange]  Dashboard_N mode switch
 * @param {boolean} [props.linked]                       Dashboard_N: the Link chip's state
 * @param {(on: boolean) => void} [props.onLinkedChange]
 * @param {string} [props.linkedYearMonth]               Dashboard_N: the Month view's month
 * @param {(yearMonth: string) => void} [props.onPickMonth]
 * @param {Object<string, string[]>} [props.outlierPayees]  Dashboard_N (#2144): outlier payee names by month, as fetched so far
 * @param {(yearMonth: string) => void} [props.onNeedOutlierPayees]  Dashboard_N (#2144): asked once per month the Raw caption shows and the map lacks
 */
const NetHistoryBars = ({
  months, anchorYearMonth, currentYearMonth, strings, locale, overlay, onOverlayChange,
  series = 'baseline', onSeriesChange, linked = false, onLinkedChange, linkedYearMonth, onPickMonth,
  outlierPayees = {}, onNeedOutlierPayees,
}) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const { isMasked } = useMasking();
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const [place, setPlace] = useState(null);
  const rowRef = useRef(null);
  const slotRefs = useRef([]);
  const nMode = typeof onSeriesChange === 'function';
  const raw = nMode && series === 'raw';

  const anchorIndex = Math.max(0, months.findIndex((m) => m.yearMonth === anchorYearMonth));
  const currentIndex = months.findIndex((m) => m.yearMonth === currentYearMonth);
  const linkedIndex = nMode && linked ? months.findIndex((m) => m.yearMonth === linkedYearMonth) : -1;
  // A pin outranks hover, so a clicked month holds while the pointer moves on
  // (#1982 follow-up); hover only previews while nothing is pinned. Linked,
  // the Month view's month is the pin.
  const shownIndex = linkedIndex >= 0 ? (hover ?? linkedIndex) : (pinned ?? hover ?? anchorIndex);
  const right = alignsRight(shownIndex, months.length);
  // Unpinning also clears hover: a touch tap leaves hover set on the tapped
  // slot with no leave event, which would keep the caption there.
  const togglePin = (i) => {
    if (linkedIndex >= 0) {
      if (i !== linkedIndex) onPickMonth(months[i].yearMonth);
      return;
    }
    if (pinned === i) {
      setPinned(null);
      setHover(null);
    } else {
      setPinned(i);
    }
  };
  const hasData = (m) => (m.lineCount || 0) > 0;
  // What a bar draws: Net (plus OT with the toggle on) on the classic
  // Dashboard; Baseline net or Net with transfers and outliers counted in
  // Dashboard_N mode.
  const drawCents = (m) => {
    if (!nMode) return barCents(m, overlay);
    return raw ? everythingCents(m) : baselineNetCents(m);
  };
  const { top, bottom } = scaleOf(months, drawCents);
  const range = top - bottom;
  const usable = CHART_HEIGHT - 2 * PAD;
  // A whole pixel, so the baseline stays crisp and the bars meet it; an empty
  // window keeps its baseline in the middle.
  const zeroY = range > 0 ? Math.round(PAD + (top / range) * usable) : CHART_HEIGHT / 2;
  // Recomputed from the same scale, so each tab gets its own lines.
  const grid = gridSteps(top, bottom);
  const amountColor = (cents) => (cents === 0 ? palette.text.dim : cents > 0 ? palette.success.main : palette.error.main);
  const money = (cents) => <MaskedCurrency value={cents / 100} style={{ color: amountColor(cents) }} />;
  // Dashboard_N colors (#2207): the caption's Baseline and Raw, and the
  // table's Raw, white above zero and red below; the table's Baseline,
  // Transfers and Outliers white at any sign; zero dim. The bars keep green
  // and red.
  const whiteRed = (cents) => <MaskedCurrency value={cents / 100} style={{ color: resolveAmountColor(palette, 'row', cents < 0, cents === 0) }} />;
  const white = (cents) => <MaskedCurrency value={cents / 100} style={{ color: resolveAmountColor(palette, 'neutral', cents < 0, cents === 0) }} />;
  // No word here: the color and the accounting parens carry the sign, and the
  // bar's side of the baseline repeats it (owner, 2026-10-01).
  const valueOf = (m) => (hasData(m) ? money(m.netCents || 0) : strings['costOfLiving.noEntries']);
  const hasOneTime = (m) => Boolean(m.oneTimeOutCents || m.oneTimeInCents);
  const oneTimeOf = (m) => money(oneTimeNetCents(m));
  // Net + OT (#2001), signed: net one-time income raises it, net one-time
  // spend lowers it.
  const netPlusOneTimeOf = (m) => money((m.netCents || 0) + oneTimeNetCents(m));
  // The current month reads "<label> so far" wherever a month is named.
  const nameOf = (m, label) => (m.yearMonth === currentYearMonth
    ? interpolate(strings['costOfLiving.soFar'], { month: label })
    : label);
  const title = months.length ? historyRange(months[0].yearMonth, locale) : '';
  const netLabel = strings['costOfLiving.netColumn'];
  const otLabel = strings['costOfLiving.outliers'];
  const baselineLabel = strings['dashboardN.chart.baseline'];
  const rawLabel = strings['dashboardN.chart.raw'];
  const outliersLabel = strings['dashboardN.chart.outliers'];

  // The partial month stays out of the fit; it is always last, so the
  // finished months keep their slot indexes.
  const finished = currentIndex >= 0 ? months.slice(0, currentIndex) : months;
  const netFit = netTrend(finished);
  // The line follows the bars (#1978); the sentence stays on Net on the
  // classic Dashboard and follows the active tab in Dashboard_N mode.
  const drawnFit = netTrend(finished.map((m) => ({ ...m, netCents: drawCents(m) })));
  const trend = nMode || overlay ? drawnFit : netFit;
  const sentenceFit = nMode ? drawnFit : netFit;
  let trendText = null;
  if (sentenceFit) {
    const key = sentenceFit.slopeCents > 0
      ? 'costOfLiving.trendUp'
      : sentenceFit.slopeCents < 0 ? 'costOfLiving.trendDown' : 'costOfLiving.trendFlat';
    const text = interpolate(strings[key], { amount: formatCurrency(Math.abs(sentenceFit.slopeCents) / 100) });
    trendText = isMasked ? maskValue(text) : text;
  }

  // Follow the shown month: over the left half the caption starts at its bar's
  // left side, over the right half it ends at its bar's right side. Measured on
  // every change and on window resize; no ResizeObserver needed.
  useLayoutEffect(() => {
    if (asTable) return undefined;
    const measure = () => {
      const row = rowRef.current;
      const slot = slotRefs.current[shownIndex];
      if (!row || !slot) return;
      const rowBox = row.getBoundingClientRect();
      const slotBox = slot.getBoundingClientRect();
      setPlace(right
        ? { right: rowBox.right - slotBox.right + BAR_INSET }
        : { left: slotBox.left - rowBox.left + BAR_INSET });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [asTable, shownIndex, months, right]);

  // #2144: on Raw the caption names the shown month's outlier payees; the
  // page fetches a month the first time it is shown and keeps it. A hook, so
  // it sits above the table view's early return.
  const shownYearMonth = months[shownIndex]?.yearMonth;
  const shownHasLines = (months[shownIndex]?.lineCount || 0) > 0;
  useEffect(() => {
    if (raw && shownHasLines && onNeedOutlierPayees && !(shownYearMonth in outlierPayees)) onNeedOutlierPayees(shownYearMonth);
  }, [raw, shownHasLines, shownYearMonth, outlierPayees, onNeedOutlierPayees]);

  const textButtonSx = {
    border: 0,
    background: 'none',
    padding: 0,
    font: 'inherit',
    fontSize: `${ds.font.dim}px`,
    color: palette.interactive,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  };

  const viewToggle = (
    <Box component="button" type="button" onClick={() => setAsTable((t) => !t)} sx={textButtonSx}>
      {asTable ? strings['costOfLiving.asChart'] : strings['costOfLiving.asTable']}
    </Box>
  );

  // The OT toggle wears the ledger line OT chip (8.15) when on, a grey outline
  // of the same size when off; the label reads "O" either way.
  const tone = palette.entryType.onetime;
  const chipSx = (on, fg, bg) => ({
    boxSizing: 'border-box',
    minWidth: CHIP_WIDTH,
    height: CHIP_HEIGHT,
    padding: '3px 7px',
    fontSize: '11px',
    fontWeight: ds.font.weightMedium,
    letterSpacing: '.04em',
    lineHeight: '14px',
    textAlign: 'center',
    borderRadius: '3px',
    border: 0,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: on ? bg : 'transparent',
    color: on ? fg : palette.text.dim,
    boxShadow: on ? 'none' : `inset 0 0 0 1px ${palette.divider}`,
    transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}, color ${ds.motion.fast} ${ds.motion.easeOut}`,
    '&:focus-visible': { outline: `1.5px solid ${palette.interactive}`, outlineOffset: '2px' },
  });
  const otToggle = (
    <Box
      component="button"
      type="button"
      aria-pressed={overlay}
      aria-label={strings['costOfLiving.otAria']}
      onClick={() => onOverlayChange(!overlay)}
      sx={chipSx(overlay, tone.fg, tone.bg)}
    >
      {strings['entryType.abbr.onetime']}
    </Box>
  );

  // Dashboard_N heading: the title on the left, carrying the active series'
  // note on a 2 second hover (#2179); the Link chip, the Baseline / Raw
  // control and the view toggle on the right, wrapping under them on a phone.
  const orange = palette.warningSecondary;
  const linkColor = palette.cat.checking;
  const seriesNote = strings[raw ? 'dashboardN.chart.raw.note' : 'dashboardN.chart.baseline.note'];
  const heading = nMode && (
    <Box
      data-testid="net-history-heading"
      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: `${ds.space[3]}px`, mb: `${ds.space[3]}px` }}
    >
      <Typography component="h3" sx={{ m: 0, minWidth: 0, fontSize: `${ds.font.meta}px`, fontWeight: ds.font.weightMedium, color: palette.text.secondary }}>
        <FigureNote note={seriesNote} enterDelay={2000} data-testid="net-history-title">{strings['dashboardN.chart.title']}</FigureNote>
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[4]}px`, flexWrap: 'wrap' }}>
        <Box
          component="button"
          type="button"
          aria-pressed={linked}
          title={strings['dashboardN.chart.link.note']}
          onClick={() => onLinkedChange && onLinkedChange(!linked)}
          sx={chipSx(linked, linkColor, palette.tint.checking)}
        >
          <IconLink size={13} aria-hidden />
          {strings['dashboardN.chart.link']}
        </Box>
        <Segmented
          inCard
          ariaLabel={strings['dashboardN.chart.series']}
          value={series}
          onChange={onSeriesChange}
          options={[
            { value: 'baseline', label: baselineLabel, title: strings['dashboardN.chart.baseline.note'] },
            { value: 'raw', label: rawLabel, title: strings['dashboardN.chart.raw.note'], tone: { fg: orange, bg: alpha(orange, 0.14) } },
          ]}
        />
        {viewToggle}
      </Box>
    </Box>
  );

  const titleSx = { fontSize: `${ds.font.dim}px`, color: palette.text.dim, whiteSpace: 'nowrap' };

  if (asTable) {
    const cellSx = {
      textAlign: 'left',
      padding: `${ds.space[1]}px ${ds.space[3]}px ${ds.space[1]}px 0`,
      borderBottom: `1px solid ${palette.divider}`,
      fontSize: `${ds.font.dim}px`,
    };
    const headSx = { ...cellSx, color: palette.text.dim, fontWeight: ds.font.weightRegular };
    const numSx = { ...cellSx, color: palette.text.dim, fontVariantNumeric: 'tabular-nums' };
    const columns = nMode
      ? [baselineLabel, strings['dashboardN.transfers'], otLabel, rawLabel]
      : [strings['costOfLiving.netColumn'], otLabel, `${netLabel} + ${otLabel}`];
    const cellsOf = (m) => {
      if (nMode) {
        if (!hasData(m)) return [strings['costOfLiving.noEntries'], null, null, null];
        return [
          white(baselineNetCents(m)),
          white(m.transferNetCents || 0),
          white(outliersCents(m)),
          whiteRed(everythingCents(m)),
        ];
      }
      return [valueOf(m), hasOneTime(m) ? oneTimeOf(m) : null, hasData(m) ? netPlusOneTimeOf(m) : null];
    };
    return (
      <Box>
        {heading}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: `${ds.space[3]}px` }}>
          <Box component="span" sx={titleSx}>{title}</Box>
          {!nMode && viewToggle}
        </Box>
        {/* The table always carries all three values; the OT toggle shows
            only with the chart (owner, #2001). */}
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Box component="th" scope="col" sx={headSx}>{strings['costOfLiving.month']}</Box>
              {columns.map((c) => <Box key={c} component="th" scope="col" sx={headSx}>{c}</Box>)}
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.yearMonth}>
                <Box component="td" sx={{ ...cellSx, color: palette.text.secondary }}>
                  {nameOf(m, monthLabel(m.yearMonth, locale))}
                </Box>
                {cellsOf(m).map((cell, i) => <Box key={i} component="td" sx={numSx}>{cell}</Box>)}
              </tr>
            ))}
          </tbody>
        </Box>
        {trendText && (
          <Box data-testid="net-history-trend-text" sx={{ ...titleSx, pt: `${ds.space[2]}px` }}>
            {trendText}
          </Box>
        )}
      </Box>
    );
  }

  const shown = months[shownIndex];
  const pxOf = (cents) => Math.max(2, Math.round((Math.abs(cents) / range) * usable));
  // The trend's height on the bars' scale, kept inside the chart: a fitted end
  // can reach past the tallest bar.
  const trendY = (cents) => (range > 0
    ? Math.min(CHART_HEIGHT, Math.max(0, zeroY - (cents / range) * usable))
    : zeroY);
  const heightMotion = `height ${ds.motion.base} ${ds.motion.easeOut}, bottom ${ds.motion.base} ${ds.motion.easeOut}, top ${ds.motion.base} ${ds.motion.easeOut}`;
  const captionPos = right
    ? { left: 'auto', right: place?.right ?? 0 }
    : { left: place?.left ?? 0, right: 'auto' };
  const lineSx = { display: 'block', height: CAPTION_LINE };
  const outlierNames = raw && shownYearMonth ? outlierPayees[shownYearMonth] || [] : [];
  // One line on desktop, two on a phone, clipped with an ellipsis; the full
  // list opens on hover, focus and tap. The row reserves the room (below), so
  // the chart never moves.
  const outliersLine = outlierNames.length > 0 && (
    <FigureNote
      note={`${outliersLabel}: ${outlierNames.join(', ')}`}
      data-testid="net-history-caption-outliers"
      sx={{
        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: { xs: 2, md: 1 },
        overflow: 'hidden', whiteSpace: 'normal', maxWidth: '100%', lineHeight: `${CAPTION_LINE}px`,
      }}
    >
      <Box component="span" sx={{ color: palette.entryType.onetime.fg, fontWeight: ds.font.weightMedium }}>{outliersLabel}:</Box>
      {' '}{outlierNames.join(', ')}
    </FigureNote>
  );
  const barColor = (up) => (raw ? orange : up ? palette.success.main : palette.error.main);
  const chartAria = nMode ? strings['dashboardN.chart.aria'] : strings['costOfLiving.chartAria'];

  const captionBody = () => {
    if (!hasData(shown)) return <Box component="span" sx={lineSx}>{strings['costOfLiving.noEntries']}</Box>;
    if (nMode) {
      return (
        <>
          <Box component="span" sx={lineSx}>{baselineLabel}: {whiteRed(baselineNetCents(shown))}</Box>
          <Box component="span" sx={lineSx}>{rawLabel}: {whiteRed(everythingCents(shown))}</Box>
          {outliersLine}
        </>
      );
    }
    return (
      <>
        <Box component="span" sx={lineSx}>{netLabel}: {valueOf(shown)}</Box>
        {overlay && (
          <>
            <Box component="span" sx={lineSx}>{otLabel}: {oneTimeOf(shown)}</Box>
            <Box component="span" sx={lineSx}>{`${netLabel} + ${otLabel}`}: {netPlusOneTimeOf(shown)}</Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box>
      {heading}
      <Box
        ref={rowRef}
        sx={{ position: 'relative', height: nMode ? { xs: CAPTION_LINE * 5, md: CAPTION_LINE * 4 } : CAPTION_LINE * 4, mb: `${ds.space[3]}px`, fontSize: `${ds.font.dim}px` }}
      >
        {shown && (
          <Box
            data-testid="net-history-caption"
            aria-live="polite"
            sx={{
              position: 'absolute',
              top: 0,
              ...captionPos,
              display: 'flex',
              flexDirection: 'column',
              alignItems: right ? 'flex-end' : 'flex-start',
              textAlign: right ? 'right' : 'left',
              lineHeight: `${CAPTION_LINE}px`,
              color: palette.text.secondary,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ ...lineSx, display: 'inline-flex', alignItems: 'center', gap: '4px', color: linkedIndex >= 0 && shownIndex === linkedIndex ? linkColor : 'inherit' }}>
              {nameOf(shown, shortMonthLabel(shown.yearMonth, locale))}
              {linkedIndex >= 0 && shownIndex === linkedIndex && <IconLink size={12} aria-hidden data-testid="net-history-caption-link" />}
            </Box>
            {captionBody()}
          </Box>
        )}
      </Box>
      <Box
        role="img"
        aria-label={trendText ? `${chartAria} ${trendText}.` : chartAria}
        onMouseLeave={() => setHover(null)}
        sx={{ position: 'relative', display: 'flex', gap: '2px', height: CHART_HEIGHT }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: zeroY,
            height: '1px',
            backgroundColor: palette.borderStrong,
            // The baseline glides with the bars when the toggle rescales (#2008).
            transition: `top ${ds.motion.base} ${ds.motion.easeOut}`,
          }}
        />
        {grid.map((cents) => (
          <Box
            key={cents}
            aria-hidden
            data-testid="net-history-gridline"
            data-cents={cents}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: Math.round(zeroY - (cents / range) * usable),
              height: 0,
              borderTop: `1px dashed ${alpha(palette.text.primary, 0.07)}`,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {!isMasked && (
              <Box
                component="span"
                data-testid="net-history-gridline-label"
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '-14px',
                  pr: '4px',
                  fontSize: '10px',
                  lineHeight: '13px',
                  color: palette.text.dim,
                  backgroundColor: alpha(palette.surface.raised, 0.85),
                }}
              >
                {gridLabel(cents)}
              </Box>
            )}
          </Box>
        ))}
        {months.map((m, i) => {
          const cents = drawCents(m);
          const up = cents > 0;
          const lit = i === linkedIndex;
          const base = i === anchorIndex || lit ? 1 : i === currentIndex ? CURRENT_OPACITY : DIM_OPACITY;
          const active = i === shownIndex;
          const anchor = i === anchorIndex ? 'true' : 'false';
          return (
            <Box
              key={m.yearMonth}
              ref={(el) => { slotRefs.current[i] = el; }}
              data-testid="net-history-slot"
              data-year-month={m.yearMonth}
              data-linked={lit ? 'true' : undefined}
              onMouseEnter={() => setHover(i)}
              onClick={() => togglePin(i)}
              sx={{
                position: 'relative',
                flex: '1 1 0',
                minWidth: 0,
                cursor: 'pointer',
                borderRadius: '3px',
                backgroundColor: active && (hover != null || pinned != null || lit) ? palette.action.hover : 'transparent',
              }}
            >
              {hasData(m) && cents !== 0 && (
                <Box
                  data-testid="net-history-bar"
                  data-sign={up ? 'excess' : 'overspent'}
                  data-anchor={anchor}
                  data-cents={Math.abs(cents)}
                  sx={{
                    position: 'absolute',
                    left: BAR_INSET,
                    right: BAR_INSET,
                    height: pxOf(cents),
                    ...(up ? { bottom: CHART_HEIGHT - zeroY } : { top: zeroY + 1 }),
                    borderRadius: up ? `${BAR_RADIUS}px ${BAR_RADIUS}px 0 0` : `0 0 ${BAR_RADIUS}px ${BAR_RADIUS}px`,
                    backgroundColor: barColor(up),
                    opacity: base,
                    transition: heightMotion,
                  }}
                />
              )}
              {hasData(m) && cents === 0 && (
                <Box
                  data-testid="net-history-bar"
                  data-sign="zero"
                  data-anchor={anchor}
                  sx={{
                    position: 'absolute', left: BAR_INSET, right: BAR_INSET, top: zeroY - 1, height: '3px',
                    backgroundColor: palette.text.dim,
                  }}
                />
              )}
            </Box>
          );
        })}
        {trend && (
          // Slot centers sit at (i + 0.5) of n equal slots; the 2px gaps shift
          // that by under a pixel, so no layout is measured.
          <svg
            aria-hidden
            data-testid="net-history-trend"
            viewBox={`0 0 ${months.length} ${CHART_HEIGHT}`}
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          >
            <line
              x1={trend.fromIndex + 0.5}
              y1={trendY(trend.fromCents)}
              x2={trend.toIndex + 0.5}
              y2={trendY(trend.toCents)}
              stroke={palette.text.dim}
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
      </Box>
      <Box aria-hidden sx={{ display: 'flex', gap: '2px', mt: `${ds.space[1]}px` }}>
        {months.map((m, i) => (
          <Typography
            key={m.yearMonth}
            component="span"
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              fontSize: '11px',
              color: palette.text.dim,
              whiteSpace: 'nowrap',
              overflow: 'visible',
              // Centered by flex, not text-align: a label wider than a phone's
              // slot then spills evenly to both sides instead of only to the
              // right, so neighbours never touch and the end labels stay
              // inside the card.
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {i % 3 === 0 ? shortMonthLabel(m.yearMonth, locale) : ''}
          </Typography>
        ))}
      </Box>
      {!nMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: `${ds.space[2]}px` }}>
          {otToggle}
          {viewToggle}
        </Box>
      )}
    </Box>
  );
};

export default NetHistoryBars;
